/**
 * Tracker-style pattern data (07 §2). A track is written as bars of sixteen tokens, one per
 * sixteenth note, which is about as close to reading a tracker screen as plain text gets:
 *
 *   `D4 - - D4 D4 - F4 - A4 - - - - - A4 G4`     melodic: note on, `-` holds it, `.` rests
 *   `X . . x x . x . X . . x x . o o`             percussion: accent, hit, ghost note
 *
 * A note may carry `^` (accent) or `_` (soft). Parsing happens once, at module load; malformed
 * data throws there, so a typo in a pattern fails the unit tests rather than a player's session.
 */

import type { InstrumentId } from './instruments';
import { MIDI_MAX, MIDI_MIN, parseNote } from './theory';

export const STEPS_PER_BAR = 16;
export const STEPS_PER_BEAT = 4;

export interface NoteEvent {
  /** Sixteenth-note index from the start of the pattern. */
  step: number;
  midi: number;
  /** Length in steps. */
  len: number;
  /** 0..1. */
  vel: number;
}

/** What switches a track on: the adaptive layers of 07 §4. `always` tracks are never muted. */
export type LayerId =
  | 'always' | 'roll' | 'drone' | 'motif' | 'hat' | 'bass' | 'drums' | 'lead' | 'urgency' | 'extraction' | 'lift';

export interface Track {
  name: string;
  instrument: InstrumentId;
  layer: LayerId;
  gain: number;
  /** Length of this track's own data, which must equal the song's. */
  steps: number;
  notes: NoteEvent[];
}

export interface Song {
  name: string;
  bpm: number;
  steps: number;
  tracks: Track[];
}

const VELOCITY = { accent: 1, normal: 0.8, soft: 0.5, ghost: 0.35, hit: 0.7 } as const;
/** Percussion has no pitch of its own; MIDI 60 plays the drum buffer at its natural rate. */
export const DRUM_MIDI = 60;

export function parseBars(bars: readonly string[]): { steps: number; notes: NoteEvent[] } {
  const notes: NoteEvent[] = [];
  let open: NoteEvent | null = null;
  bars.forEach((bar, b) => {
    const tokens = bar.trim().split(/\s+/);
    if (tokens.length !== STEPS_PER_BAR) {
      throw new Error(`bar ${b + 1} has ${tokens.length} steps, not ${STEPS_PER_BAR}: "${bar}"`);
    }
    tokens.forEach((token, i) => {
      const step = b * STEPS_PER_BAR + i;
      if (token === '-') {
        if (!open) throw new Error(`bar ${b + 1} step ${i + 1}: "-" with no note to hold`);
        open.len++;
        return;
      }
      open = null;
      if (token === '.') return;
      if (token === 'X' || token === 'x' || token === 'o') {
        const vel = token === 'X' ? VELOCITY.accent : token === 'x' ? VELOCITY.hit : VELOCITY.ghost;
        notes.push({ step, midi: DRUM_MIDI, len: 1, vel });
        return;
      }
      const mark = token.endsWith('^') ? 'accent' : token.endsWith('_') ? 'soft' : 'normal';
      const midi = parseNote(mark === 'normal' ? token : token.slice(0, -1));
      if (midi === null) throw new Error(`bar ${b + 1} step ${i + 1}: "${token}" is not a note`);
      open = { step, midi, len: 1, vel: VELOCITY[mark] };
      notes.push(open);
    });
  });
  return { steps: bars.length * STEPS_PER_BAR, notes };
}

/** Build a track from its bars. */
export function track(
  name: string,
  instrument: InstrumentId,
  layer: LayerId,
  gain: number,
  bars: readonly string[],
): Track {
  return { name, instrument, layer, gain, ...parseBars(bars) };
}

/**
 * A copy of a TONAL track with every note passed through `alter` (scale colour) and then moved
 * by `semitones`. Percussion tracks must not come through here: their "pitch" is a playback rate.
 */
export function transposed(t: Track, semitones: number, alter: (midi: number) => number = (m) => m): Track {
  return { ...t, notes: t.notes.map((n) => ({ ...n, midi: alter(n.midi) + semitones })) };
}

/** Everything wrong with a song, as sentences; empty = well-formed. */
export function validateSong(song: Song): string[] {
  const problems: string[] = [];
  if (song.bpm < 40 || song.bpm > 200) problems.push(`${song.name}: bpm ${song.bpm} out of range`);
  const wholeBars = song.steps > 0 && song.steps % STEPS_PER_BAR === 0;
  if (!wholeBars) problems.push(`${song.name}: ${song.steps} steps is not whole bars`);
  for (const t of song.tracks) {
    const where = `${song.name}/${t.name}`;
    if (t.steps !== song.steps) problems.push(`${where}: ${t.steps} steps, song has ${song.steps}`);
    if (t.gain <= 0 || t.gain > 1) problems.push(`${where}: gain ${t.gain}`);
    let busyUntil = 0;
    for (const n of t.notes) {
      if (n.step < busyUntil) problems.push(`${where}: note at step ${n.step} overlaps the one before`);
      if (n.len < 1 || n.step + n.len > song.steps) problems.push(`${where}: note at step ${n.step} runs past the end`);
      const inRange = n.midi >= MIDI_MIN && n.midi <= MIDI_MAX;
      if (!inRange) problems.push(`${where}: MIDI ${n.midi} at step ${n.step} out of range`);
      if (n.vel <= 0 || n.vel > 1) problems.push(`${where}: velocity ${n.vel} at step ${n.step}`);
      busyUntil = n.step + n.len;
    }
  }
  return problems;
}
